/**
 * Snackbar global — un toast à la fois (le précédent est replacé).
 *
 * iOS pattern : message court avec optionnel bouton d'action (souvent
 * "Annuler" pour permettre l'undo d'une action destructive). Auto-dismiss
 * après 4 s par défaut. Tap-action exécute le callback puis dismiss.
 *
 * Usage :
 *   showToast({ message: "Archivé", action: { label: "Annuler", run: () => unarchive() } });
 */

interface ToastAction {
  label: string;
  run: () => void;
}

interface ToastState {
  message: string;
  action: ToastAction | null;
  visible: boolean;
  /** Compteur incrémenté à chaque show — sert à invalider les timers en cours. */
  generation: number;
}

export const toastState = $state<ToastState>({
  message: "",
  action: null,
  visible: false,
  generation: 0,
});

let dismissTimer: ReturnType<typeof setTimeout> | null = null;

export interface ShowToastOptions {
  message: string;
  action?: ToastAction;
  /** ms avant auto-dismiss. 0 = persistant. Default 4000. */
  duration?: number;
}

export function showToast(opts: ShowToastOptions): void {
  if (dismissTimer) clearTimeout(dismissTimer);
  toastState.message = opts.message;
  toastState.action = opts.action ?? null;
  toastState.visible = true;
  toastState.generation += 1;

  const ms = opts.duration ?? 4000;
  if (ms > 0) {
    const gen = toastState.generation;
    dismissTimer = setTimeout(() => {
      // Garde-fou : si entretemps un autre toast a remplacé, on ne touche pas
      if (toastState.generation === gen) dismissToast();
    }, ms);
  }
}

export function dismissToast(): void {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  toastState.visible = false;
}

/** Tap sur l'action — exécute le callback puis dismiss. */
export function runToastAction(): void {
  const action = toastState.action;
  dismissToast();
  action?.run();
}
